import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import TenantLayout from '../components/TenantLayout';

const TenantDashboard = () => {
  const [parks, setParks] = useState([]);
  const [analytics, setAnalytics] = useState({});

  useEffect(() => {
    fetchTenantAnalytics();
  }, []);

  const fetchTenantAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Fetch parks
      const parksResponse = await axios.get(`${API_BASE_URL}/api/tickets`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'x-tenant-id': user.tenantId || 'default'
        }
      });
      
      const tickets = parksResponse.data.tickets || [];
      setParks(tickets);
      
      // Fetch real analytics
      const analyticsResponse = await axios.get(`${API_BASE_URL}/api/analytics`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'x-tenant-id': user.tenantId || 'default'
        },
        params: { parkId: 'all' }
      });
      
      const realAnalytics = analyticsResponse.data.analytics || {};
      const avgRevenuePerBooking = realAnalytics.totalBookings > 0 
        ? realAnalytics.totalRevenue / realAnalytics.totalBookings 
        : 0;
      
      setAnalytics({
        totalVisitors: realAnalytics.totalBookings || 0,
        totalRevenue: realAnalytics.totalRevenue || 0,
        totalBookings: realAnalytics.totalBookings || 0,
        avgTicketPrice: Math.round(avgRevenuePerBooking)
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setParks([]);
      setAnalytics({ totalVisitors: 0, totalRevenue: 0, totalBookings: 0, avgTicketPrice: 0 });
    }
  };

  return (
    <TenantLayout userRole="tenant_admin">
      <header style={{
        background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #10b981 100%)',
        color: 'white',
        padding: '32px',
        boxShadow: '0 10px 40px rgba(6, 182, 212, 0.2)'
      }}>
        <div>
          <h1 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '8px' }}>Tenant Dashboard</h1>
          <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.1rem' }}>Manage your {parks.length} parks and operations</p>
        </div>
      </header>

      <main className="flex-1 p-8 overflow-auto">
        <div className="space-y-8 -mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Total Visitors', value: analytics.totalVisitors?.toLocaleString() || '0', icon: '👥', color: '#06b6d4' },
              { title: 'Total Revenue', value: `₹${(analytics.totalRevenue || 0).toLocaleString()}`, icon: '💰', color: '#10b981' },
              { title: 'Total Bookings', value: analytics.totalBookings?.toLocaleString() || '0', icon: '📋', color: '#0891b2' },
              { title: 'Avg Ticket Price', value: `₹${analytics.avgTicketPrice || 0}`, icon: '🎫', color: '#059669' }
            ].map((stat, index) => (
              <div key={index} style={{
                background: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255,255,255,0.3)',
                padding: '24px',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.title}</p>
                    <p style={{ fontSize: '2rem', fontWeight: '700', color: '#1e293b', marginTop: '12px' }}>{stat.value}</p>
                  </div>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    background: stat.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 8px 25px ${stat.color}40`
                  }}>
                    <span style={{ fontSize: '2rem', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}>{stat.icon}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Your Parks</h3>
                <Link to="/tenant-dashboard/parks" style={{
                  background: 'linear-gradient(45deg, #06b6d4, #10b981)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(6, 182, 212, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(6, 182, 212, 0.3)';
                }}>
                  Manage Parks
                </Link>
              </div>
              <div className="space-y-4">
                {parks.map(park => (
                  <div key={park._id} className="flex items-center justify-between p-5 bg-gradient-to-r from-gray-50 to-green-50 rounded-xl hover:from-green-50 hover:to-emerald-50 transition-all duration-300">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                        <span className="text-white text-lg">🎢</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{park.name}</p>
                        <p className="text-sm text-gray-500">{park.city} • ₹{park.price}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">{park.currentVisitors || 0} visitors</p>
                      <p className="text-sm text-green-600">{park.category.replace('_', ' ')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h3>
              <div className="space-y-4">
                <Link to="/tenant-dashboard/parks" className="w-full flex items-center space-x-4 p-4 text-left bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 rounded-xl transition-all duration-300 border border-green-100 hover:shadow-lg">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                    <span className="text-white text-lg">🎢</span>
                  </div>
                  <span className="font-semibold text-gray-700">Manage Parks</span>
                </Link>
                <Link to="/tenant-dashboard/settings" className="w-full flex items-center space-x-4 p-4 text-left bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100 rounded-xl transition-all duration-300 border border-blue-100 hover:shadow-lg">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                    <span className="text-white text-lg">⚙️</span>
                  </div>
                  <span className="font-semibold text-gray-700">Park Settings</span>
                </Link>
                <Link to="/tenant-dashboard/function-hall-calendar" className="w-full flex items-center space-x-4 p-4 text-left bg-gradient-to-r from-yellow-50 to-orange-50 hover:from-yellow-100 hover:to-orange-100 rounded-xl transition-all duration-300 border border-yellow-100 hover:shadow-lg">
                  <div className="w-10 h-10 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                    <span className="text-white text-lg">🏆</span>
                  </div>
                  <span className="font-semibold text-gray-700">Function Hall Calendar</span>
                </Link>
                <button className="w-full flex items-center space-x-4 p-4 text-left bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 rounded-xl transition-all duration-300 border border-purple-100 hover:shadow-lg">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <span className="text-white text-lg">📊</span>
                  </div>
                  <span className="font-semibold text-gray-700">View Analytics</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </TenantLayout>
  );
};

export default TenantDashboard;